import { normalize, schema } from 'normalizr'
import { createSelector } from 'reselect'
import {
  IOrganization,
  IOrganizationsState,
  IOrgObj,
  IStateMetadata,
  IStoreState,
  LOCAL_STORAGE_CURRENT_ORGANIZATION_KEY,
  IOrganizationLegalEntityState,
  IPromotion
} from '../../../types'
import UIMessages from '../../../utils/ui-messages'
import { IOrganizationActions } from '../../actions/organizations'
import * as constants from '../../constants/'

const initOrganizationsState: IOrganizationsState = {
  currentOrgIdRequest: false,
  currentOrgId: localStorage.getItem(LOCAL_STORAGE_CURRENT_ORGANIZATION_KEY) || undefined,
  currentOrgIdError: null,
  newOrgId: null,
  items: {},
  metadata: {
    fetching: false,
    fetchingError: '',
    fetchingSuccess: '',
    processing: false,
    processingError: '',
    processingSuccess: '',
    bankAccountAdded: false,
    legalEntityCompleted: false
  }
}

export function organizations(
  state: IOrganizationsState = initOrganizationsState,
  action: IOrganizationActions
): IOrganizationsState {
  const metadata = {
    fetching: false,
    fetchingError: '',
    fetchingSuccess: '',
    processing: false,
    processingError: '',
    processingSuccess: '',
    bankAccountAdded: false,
    legalEntityCompleted: false
  }
  switch (action.type) {
    case constants.ORGANIZATION_LIST_REQUEST: {
      metadata.fetching = true
      return Object.assign({}, state, { metadata })
    }
    case constants.ORGANIZATION_LIST_SUCCESS: {
      const organizationList = action.payload
      const items = Object.assign({}, state.items, organizationList)
      metadata.fetching = false
      return Object.assign({}, state, {
        items,
        metadata,
        lastUpdated: Date.now()
      })
    }
    case constants.ORGANIZATION_LIST_FAILURE: {
      metadata.fetching = false
      return Object.assign({}, state, { metadata })
    }

    case constants.CURRENT_ORG_ID_REQUEST:
      return Object.assign({}, state, {
        currentOrgIdRequest: true
      })
    case constants.SET_CURRENT_ORG_SUCCESS:
      const currentOrg = action.payload
      localStorage.setItem(LOCAL_STORAGE_CURRENT_ORGANIZATION_KEY, currentOrg.id)
      return Object.assign({}, state, {
        currentOrgIdRequest: false,
        currentOrgId: currentOrg.id,
        items: {
          ...state.items,
          [currentOrg.id]: currentOrg
        }
      })
    case constants.FETCH_ALL_ORGANIZATIONS:
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: true
        }
      })
    case constants.FETCH_ALL_ORGANIZATIONS_SUCCESS: {
      const data = action.payload
      const organization = new schema.Entity('organizations')
      const organizationSchema = [organization]
      const normalizedData = normalize(data, organizationSchema)
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: ''
        },
        items: normalizedData.entities.organizations ? normalizedData.entities.organizations : {}
      })
    }
    case constants.FETCH_ALL_ORGANIZATIONS_FAIL:
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: action.payload
        }
      })

    case constants.CREATE_ORGANIZATION:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.CREATE_ORGANIZATION_SUCCESS: {
      const organization = action.payload
      const items = Object.assign({}, state.items, organization)
      const newOrgId = Object.keys(organization).pop()
      metadata.processing = false
      metadata.processingSuccess = UIMessages.organization.organizationInfo.successfullyUpdated
      return Object.assign({}, state, { items, metadata, newOrgId })
    }
    case constants.CREATE_ORGANIZATION_FAIL:
      metadata.processing = false
      metadata.processingError = UIMessages.organization.organizationInfo.errorOnSave
      return Object.assign({}, state, { metadata })

    case constants.CREATE_ORGANIZATION_BANK_ACCOUNT_REQUEST:
      metadata.processing = true
      metadata.legalEntityCompleted = true
      return Object.assign({}, state, { metadata })
    case constants.CREATE_ORGANIZATION_BANK_ACCOUNT_SUCCESS: {
      const items = Object.assign({}, state.items)
      const organizationBankAccount = action.payload.accounts
      const orgId = (action.payload as any).orgId
      if (items[orgId]) {
        const bankAccounts = Object.assign([], items[orgId].bankAccounts, Object.keys(organizationBankAccount))
        items[orgId].bankAccounts = bankAccounts
      }
      metadata.processing = false
      metadata.processingSuccess = UIMessages.organization.bankAccount.successfullyUpdated
      metadata.bankAccountAdded = true
      metadata.legalEntityCompleted = true
      return Object.assign({}, state, { items, metadata })
    }
    case constants.CREATE_ORGANIZATION_BANK_ACCOUNT_FAILURE:
      metadata.processing = false
      metadata.processingError = action.payload
      metadata.bankAccountAdded = false
      metadata.legalEntityCompleted = true
      return Object.assign({}, state, { metadata })

    case constants.UPDATE_ORGANIZATION_BANK_ACCOUNT_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.UPDATE_ORGANIZATION_BANK_ACCOUNT_SUCCESS: {
      const { response, orgId } = action.payload
      const items = Object.assign({}, state.items)
      if (items[orgId]) {
        items[orgId].bankAccounts = Object.keys(response)
      }
      metadata.processing = false
      metadata.processingSuccess = UIMessages.organization.bankAccount.successfullyRemoved
      return Object.assign({}, state, { items, metadata })
    }
    case constants.UPDATE_ORGANIZATION_BANK_ACCOUNT_FAILURE:
      metadata.processing = false
      metadata.processingError = action.payload
      return Object.assign({}, state, { metadata })

    case constants.CREATE_ORGANIZATION_LEGAL_ENTITY_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.CREATE_ORGANIZATION_LEGAL_ENTITY_SUCCESS: {
      const items = Object.assign({}, state.items)
      const organizationLegalEntity: any = action.payload
      const orgId = organizationLegalEntity.orgId

      if (items[orgId]) {
        items[orgId].legalEntity = organizationLegalEntity
      }
      metadata.processing = false
      metadata.processingSuccess = UIMessages.organization.legalEntity.successfullyUpdated
      metadata.legalEntityCompleted = true
      return Object.assign({}, state, { items, metadata })
    }
    case constants.CREATE_ORGANIZATION_LEGAL_ENTITY_FAILURE:
      metadata.processing = false
      metadata.processingError = action.payload
      metadata.legalEntityCompleted = false
      return Object.assign({}, state, { metadata })
    case constants.REMOVE_ORGANIZATION_BANK_ACCOUNT_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.REMOVE_ORGANIZATION_BANK_ACCOUNT_SUCCESS: {
      const { organizationBankAccountId, orgId } = action.payload
      const organization = Object.assign({}, state.items[orgId])
      if (organization && organization.bankAccounts) {
        organization.bankAccounts.splice(organization.bankAccounts.indexOf(organizationBankAccountId), 1)
      }
      const items = Object.assign({}, state.items)
      metadata.processing = false
      metadata.processingSuccess = UIMessages.organization.bankAccount.successfullyRemoved
      return Object.assign({}, state, { items, metadata })
    }
    case constants.REMOVE_ORGANIZATION_BANK_ACCOUNT_FAILURE:
      metadata.processing = false
      metadata.processingError = action.payload
      return Object.assign({}, state, { metadata })
    case constants.UPDATE_ORGANIZATION_LEGAL_ENTITY_REQUEST:
      metadata.processing = true
      return Object.assign({}, state, { metadata })
    case constants.UPDATE_ORGANIZATION_LEGAL_ENTITY_SUCCESS: {
      const items = Object.assign({}, state.items)
      const legalEntity = action.payload
      const orgId = legalEntity.orgId
      // if (items[orgId]) {
      //   const bankAccounts = Object.assign([], items[orgId].bankAccounts, Object.keys(organizationBankAccount))
      //   items[orgId].bankAccounts = bankAccounts
      // }
      metadata.processing = false
      metadata.processingSuccess = UIMessages.organization.bankAccount.successfullyUpdated
      return Object.assign({}, state, { items, metadata })
    }
    case constants.UPDATE_ORGANIZATION_LEGAL_ENTITY_FAILURE:
      metadata.processing = false
      metadata.processingError = action.payload
      return Object.assign({}, state, { metadata })
    case constants.FETCH_SINGLE_ORGANIZATION:
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: true
        }
      })
    case constants.FETCH_SINGLE_ORGANIZATION_SUCCESS: {
      const organization = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: ''
        },
        items: {
          ...state.items,
          [organization.id]: organization
        }
      })
    }
    case constants.FETCH_SINGLE_ORGANIZATION_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: error
        }
      })
    }
    case constants.ORG_UPDATE_REQUEST:
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: true
        }
      })
    case constants.ORG_UPDATE_SUCCESS:
      const organization = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingSuccess: UIMessages.organization.organizationInfo.successfullyUpdated,
          processingError: ''
        },
        items: {
          ...state.items,
          [organization.id]: organization
        }
      })
    case constants.ORG_UPDATE_FAIL:
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          processing: false,
          processingError: UIMessages.organization.organizationInfo.errorOnSave
        }
      })
    case constants.ORG_SEARCH_REQUEST:
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: true
        }
      })
    case constants.ORG_SEARCH_SUCCESS: {
      const items = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: ''
        },
        items
      })
    }
    case constants.ORG_SEARCH_FAIL: {
      const error = action.payload
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false,
          fetchingError: error
        }
      })
    }
    case constants.ORG_LIST_BY_DRIVER_SUCCESS: {
      const organizations = action.payload
      return Object.assign({}, state, {
        items: {
          ...state.items,
          ...organizations
        }
      })
    }
    case constants.SWITCH_ORGANIZATION: {
      const currentOrgId = action.payload
      localStorage.setItem(LOCAL_STORAGE_CURRENT_ORGANIZATION_KEY, currentOrgId)
      return Object.assign({}, state, {
        currentOrgId
      })
    }

    case constants.ORGANIZATION_GET_SUBSCRIPTION:
    case constants.ORGANIZATION_CREATE_SUBSCRIPTION:
    case constants.ORGANIZATION_UPDATE_SUBSCRIPTION: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: true
        }
      })
    }

    case constants.ORGANIZATION_GET_SUBSCRIPTION_SUCCESS:
    case constants.ORGANIZATION_CREATE_SUBSCRIPTION_SUCCESS:
    case constants.ORGANIZATION_UPDATE_SUBSCRIPTION_SUCCESS: {
      return Object.assign({}, state, {
        items: {
          ...state.items,
          [action.payload.organizationId]: {
            ...state.items[action.payload.organizationId],
            subscription: action.payload
          }
        },
        metadata: {
          ...state.metadata,
          fetching: false
        }
      })
    }

    case constants.ORGANIZATION_GET_SUBSCRIPTION_FAILURE:
    case constants.ORGANIZATION_CREATE_SUBSCRIPTION_FAIL:
    case constants.ORGANIZATION_UPDATE_SUBSCRIPTION_FAIL: {
      return Object.assign({}, state, {
        metadata: {
          ...state.metadata,
          fetching: false
        }
      })
    }

    case constants.RESET_ORGANIZATION_METADATA: {
      return Object.assign({}, state, {
        metadata: {
          fetching: false,
          fetchingError: '',
          fetchingSuccess: '',
          processing: false,
          processingError: '',
          processingSuccess: '',
          bankAccountAdded: false,
          legalEntityCompleted: false
        }
      })
    }

    default:
      return state
  }
}

// Selectors

export const getOrganization = (state: IStoreState, id: string): IOrgObj => {
  return state.organizations.items[id]
}

export const getOrganizationFetching = (state: IStoreState): boolean => {
  return state.organizations.metadata.fetching
}

export const getOrganizationFetchingError = (state: IStoreState): string => {
  return state.organizations.metadata.fetchingError
}

export const getOrganizationProcessing = (state: IStoreState): boolean => {
  return state.organizations.metadata.processing
}

export const getOrganizationProcessingError = (state: IStoreState): string => {
  return state.organizations.metadata.processingError
}

export const getOrganizationListFetching = (state: IStoreState) => {
  return state.organizations.metadata.fetching
}

export const getOrganizationListFetchingError = (state: IStoreState) => {
  return state.organizations.metadata.fetchingError
}

export const getOrganizationList = (state: IStoreState) => {
  if (!state.organizations.items) {
    return []
  }
  return Object.values(state.organizations.items)
}

export const getCurrentOrganizationId = (state: IStoreState) => {
  return state.organizations.currentOrgId
}

export const getCurrentOrganization = createSelector(
  getOrganizationList,
  getCurrentOrganizationId,
  (organizations, currentOrgId) => {
    if (!currentOrgId || !organizations) {
      return undefined
    }
    return organizations.find(organization => organization.id === currentOrgId)
  }
)



// WEBPACK FOOTER //
// ./src/state/reducers/organizations/index.ts